import mlcontour from '@acalcutt/maplibre-contour-pmtiles'
import { invoke } from '@tauri-apps/api/core'

import { connectivityService } from '@/services/connectivityService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { getActiveOfflineOwnerUserId } from '@/services/offlineAuthBridge'
import { isTauri, isTauriAndroidMobile } from '@/services/platformService'
import { getRoutecampMapterhornTileTemplate, toNativeTileUrl } from '@/services/routecampTilesUrl'

import type maplibregl from 'maplibre-gl'

const CONTOUR_SOURCE_ID = 'routecamp-contours'
const CONTOUR_LAYER_ID = 'Contour'
const CONTOUR_INDEX_LAYER_ID = 'Contour index'
const CONTOUR_LABEL_LAYER_ID = 'Contour labels'
const CONTOUR_SOURCE_LAYER = 'contours'
export const HILLSHADE_SOURCE_ID = 'hillshade'
const HILLSHADE_LAYER_ID = 'Hillshade'
const CONTOUR_CACHE_SIZE = 192

const contourLayerIds = [CONTOUR_INDEX_LAYER_ID, CONTOUR_LAYER_ID, CONTOUR_LABEL_LAYER_ID] as const

let demSource: InstanceType<typeof mlcontour.DemSource> | null = null
let protocolsRegistered = false
let browserWorkerReferrerConfigured = false

export interface ContourOfflineContext {
  styleId: string
  sourceId: string
  ext: string
}

let activeOfflineContext: ContourOfflineContext | null = null
let onlineDemManager: InstanceType<typeof mlcontour.DemSource>['manager'] | null = null
let offlineDemManager: InstanceType<typeof mlcontour.LocalDemManager> | null = null

// Contour tile gate: while at least one hold is active (camera pan/zoom in
// progress), new contour tile requests are queued instead of executed so
// basemap tiles get network and worker priority. Already-loaded contour tiles
// keep rendering from the tile cache; only new DEM fetches + isoline
// generation are deferred until every hold is released.
let contourTileHoldCount = 0
let contourTileGateWaiters: Array<() => void> = []

function waitForContourTileGate(): Promise<void> {
  if (contourTileHoldCount === 0) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    contourTileGateWaiters.push(resolve)
  })
}

/**
 * Defers contour tile generation until the returned release function is
 * called. Holds stack (e.g. multiple maps); the release is idempotent.
 */
export function holdRoutecampContourTiles(): () => void {
  contourTileHoldCount += 1
  let released = false

  return () => {
    if (released) {
      return
    }
    released = true
    contourTileHoldCount -= 1

    if (contourTileHoldCount === 0) {
      const waiters = contourTileGateWaiters
      contourTileGateWaiters = []
      for (const resume of waiters) {
        resume()
      }
    }
  }
}

type ContourTileProtocol = InstanceType<typeof mlcontour.DemSource>['contourProtocolV4']

// Requests aborted while queued simply fall through on release; the underlying
// handler rejects fast on an already-aborted signal, which MapLibre ignores.
export function gateContourTileProtocol(protocol: ContourTileProtocol): ContourTileProtocol {
  return async (requestParameters, abortController) => {
    await waitForContourTileGate()
    return protocol(requestParameters, abortController)
  }
}

type StyleSource = {
  type?: string
  tiles?: string[]
  encoding?: string
}

type RuntimeMap = Pick<
  maplibregl.Map,
  'addLayer' | 'addSource' | 'getLayer' | 'getSource' | 'getStyle'
>

// The contour `DemSource` fetches mapterhorn DEM tiles itself (via `fetch`, off the main thread),
// so unlike the hillshade raster-dem source it never passes through MapLibre's `transformRequest`.
// Reroute its tile URL through the native `rctiles` proxy so requests carry `Referer: route.camp`
// (see `toNativeTileUrl` / `src-tauri/src/lib.rs`). Browser workers use the referrer wrapper below.
function getMapterhornDemTileUrl(): string {
  const template = getRoutecampMapterhornTileTemplate()
  if (!isTauri()) {
    return template
  }
  return toNativeTileUrl(template, isTauriAndroidMobile()) ?? template
}

function configureBrowserWorkerReferrer(): void {
  const workerUrl = mlcontour.workerUrl
  if (
    browserWorkerReferrerConfigured ||
    isTauri() ||
    typeof workerUrl !== 'string' ||
    !workerUrl.startsWith('blob:') ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return
  }

  const referrer = new URL('/', window.location.href).href
  const workerWrapper = `
const routecampFetch = self.fetch.bind(self);
self.fetch = (input, init = {}) => routecampFetch(input, {
  ...init,
  referrer: ${JSON.stringify(referrer)},
  referrerPolicy: 'origin',
});
importScripts(${JSON.stringify(workerUrl)});
`

  mlcontour.workerUrl = URL.createObjectURL(new Blob([workerWrapper], { type: 'text/javascript' }))
  browserWorkerReferrerConfigured = true
}

function getDemSource(): InstanceType<typeof mlcontour.DemSource> {
  if (!demSource) {
    // The contour package's blob worker does not inherit the page referrer. Bunny blocks those
    // referrer-less DEM requests, so bootstrap the worker through a small fetch wrapper while
    // keeping contour generation off the main thread.
    configureBrowserWorkerReferrer()
    demSource = new mlcontour.DemSource({
      id: 'routecamp-contours',
      url: getMapterhornDemTileUrl(),
      encoding: 'terrarium',
      maxzoom: 12,
      worker: true,
      cacheSize: CONTOUR_CACHE_SIZE,
      timeoutMs: 10_000,
    })
    onlineDemManager = demSource.manager
    if (activeOfflineContext) {
      demSource.manager = getOfflineDemManager()
    }
  }

  return demSource
}

type LocalDemManagerOptions = ConstructorParameters<typeof mlcontour.LocalDemManager>[0]
type GetTileFunction = NonNullable<LocalDemManagerOptions['getTile']>

// DEM fetch for offline packages: read the downloaded hillshade tile from the native offline store
// first. Package misses remain local while effectively offline; online use may fall back to
// `rctiles`. Mirrors the `rcoffline://` protocol in
// `offlineMapProtocol.ts`, which the contour DemSource cannot use because it
// fetches DEM tiles outside MapLibre's protocol/transformRequest flow.
const offlineAwareGetTile: GetTileFunction = async (z, x, y, demUrlPattern, abortController) => {
  const context = activeOfflineContext
  if (context) {
    try {
      const expectedOwnerUserId = getActiveOfflineOwnerUserId()
      if (expectedOwnerUserId === null) throw new Error('Offline data is locked')
      const result = await invoke<{ bytes: number[]; content_type: string }>('read_offline_tile', {
        styleId: context.styleId,
        sourceId: context.sourceId,
        z,
        x,
        y,
        ext: context.ext,
      })
      if (getActiveOfflineOwnerUserId() !== expectedOwnerUserId) {
        throw new Error('Offline account changed while reading a contour tile')
      }
      return { data: new Blob([new Uint8Array(result.bytes)], { type: result.content_type }) }
    } catch (error) {
      logService.debug(LOG_CATEGORIES.OFFLINE, 'Contour DEM tile not in offline cache', {
        z,
        x,
        y,
        error,
      })
      if (!connectivityService.isEffectivelyOnline.value) {
        throw error
      }
    }
  }

  const url = demUrlPattern
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y))
  const response = await fetch(url, { signal: abortController.signal })
  if (!response.ok) {
    throw new Error(`DEM tile request failed with HTTP ${response.status} for ${url}`)
  }
  return {
    data: await response.blob(),
    cacheControl: response.headers.get('cache-control') ?? undefined,
    expires: response.headers.get('expires') ?? undefined,
  }
}

function getOfflineDemManager(): InstanceType<typeof mlcontour.LocalDemManager> {
  if (!offlineDemManager) {
    // Runs decode + isoline generation on the main thread (the package's
    // supported non-worker mode; a custom getTile cannot cross the worker
    // boundary). Acceptable while an offline package is active: reads are
    // local invokes and `holdRoutecampContourTiles` already defers generation
    // during camera motion.
    offlineDemManager = new mlcontour.LocalDemManager({
      demUrlPattern: getMapterhornDemTileUrl(),
      cacheSize: CONTOUR_CACHE_SIZE,
      encoding: 'terrarium',
      maxzoom: 12,
      timeoutMs: 10_000,
      getTile: offlineAwareGetTile,
    })
  }

  return offlineDemManager
}

/**
 * Routes contour DEM fetches through the offline package store while a
 * downloaded package (with terrain) backs the active style; pass `null` to
 * restore the normal worker-backed network manager.
 */
export function setRoutecampContourOfflineContext(context: ContourOfflineContext | null): void {
  activeOfflineContext = context

  if (!demSource) {
    // DemSource not created yet — getDemSource applies the context on creation.
    return
  }

  if (context) {
    demSource.manager = getOfflineDemManager()
  } else if (onlineDemManager) {
    demSource.manager = onlineDemManager
  }
}
export function ensureRoutecampContourProtocols(
  maplibre: Pick<typeof maplibregl, 'addProtocol'>,
): void {
  if (protocolsRegistered) {
    return
  }

  // Same registrations as DemSource.setupMaplibre, but with the contour
  // protocol gated so camera movement can defer contour generation.
  const source = getDemSource()
  maplibre.addProtocol(source.sharedDemProtocolId, source.sharedDemProtocolV4)
  maplibre.addProtocol(source.contourProtocolId, gateContourTileProtocol(source.contourProtocolV4))
  protocolsRegistered = true
  logService.info(LOG_CATEGORIES.MAP, 'Registered Routecamp contour protocols')
}

// Offline packages rewrite the hillshade tiles to
// `rcoffline://<styleId>/hillshade/{z}/{x}/{y}.<ext>` (see `applyOfflineRewrite`).
const OFFLINE_HILLSHADE_TILE_PATTERN = new RegExp(`^rcoffline://[^/]+/${HILLSHADE_SOURCE_ID}/`)

function isRoutecampHillshadeSource(source: unknown): source is StyleSource {
  if (!source || typeof source !== 'object') {
    return false
  }

  const candidate = source as StyleSource
  return (
    candidate.type === 'raster-dem' &&
    candidate.encoding === 'terrarium' &&
    Array.isArray(candidate.tiles) &&
    candidate.tiles.some(
      (tile) =>
        tile === getRoutecampMapterhornTileTemplate() || OFFLINE_HILLSHADE_TILE_PATTERN.test(tile),
    )
  )
}

function supportsRoutecampContours(map: RuntimeMap): boolean {
  const source = map.getStyle().sources?.[HILLSHADE_SOURCE_ID]
  return isRoutecampHillshadeSource(source)
}

function findLayerAfter(map: RuntimeMap, layerId: string): string | undefined {
  const layers = map.getStyle().layers ?? []
  const index = layers.findIndex((layer) => layer.id === layerId)
  return index >= 0 ? layers[index + 1]?.id : undefined
}

function findFirstSymbolLayer(map: RuntimeMap): string | undefined {
  return map.getStyle().layers?.find((layer) => layer.type === 'symbol')?.id
}

function addContourSource(map: RuntimeMap): void {
  if (map.getSource(CONTOUR_SOURCE_ID)) {
    return
  }

  map.addSource(CONTOUR_SOURCE_ID, {
    type: 'vector',
    tiles: [
      getDemSource().contourProtocolUrl({
        thresholds: {
          7: [200, 1000],
          8: [200, 1000],
          10: [100, 500],
          12: [100, 500],
          14: [25, 100],
          15: [20, 100],
          16: [10, 50],
        },
        overzoom: 2,
        contourLayer: CONTOUR_SOURCE_LAYER,
        elevationKey: 'ele',
        levelKey: 'level',
        extent: 4096,
        buffer: 1,
      }),
    ],
    maxzoom: 16,
  })
}

function addContourLayers(map: RuntimeMap): void {
  const lineBeforeLayerId = findLayerAfter(map, HILLSHADE_LAYER_ID)
  const labelBeforeLayerId = findFirstSymbolLayer(map)

  if (!map.getLayer(CONTOUR_INDEX_LAYER_ID)) {
    map.addLayer(
      {
        id: CONTOUR_INDEX_LAYER_ID,
        type: 'line',
        source: CONTOUR_SOURCE_ID,
        'source-layer': CONTOUR_SOURCE_LAYER,
        minzoom: 7,
        layout: { visibility: 'visible' },
        filter: ['>', ['get', 'level'], 0],
        paint: {
          'line-color': 'rgba(118, 91, 48, 0.68)',
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7,
            0.18,
            8,
            0.24,
            10,
            0.34,
            14,
            0.5,
            16,
            0.58,
          ],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7,
            0.45,
            8,
            0.55,
            10,
            0.75,
            14,
            1.1,
            16,
            1.45,
          ],
        },
      },
      lineBeforeLayerId,
    )
  }

  if (!map.getLayer(CONTOUR_LAYER_ID)) {
    map.addLayer(
      {
        id: CONTOUR_LAYER_ID,
        type: 'line',
        source: CONTOUR_SOURCE_ID,
        'source-layer': CONTOUR_SOURCE_LAYER,
        minzoom: 9,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          visibility: 'visible',
        },
        filter: ['==', ['get', 'level'], 0],
        paint: {
          'line-color': 'rgba(137, 112, 69, 0.52)',
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9,
            0.1,
            10,
            0.14,
            12,
            0.24,
            14,
            0.34,
            16,
            0.46,
          ],
          'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.24, 10, 0.3, 14, 0.52, 16, 0.75],
        },
      },
      lineBeforeLayerId,
    )
  }

  if (!map.getLayer(CONTOUR_LABEL_LAYER_ID)) {
    map.addLayer(
      {
        id: CONTOUR_LABEL_LAYER_ID,
        type: 'symbol',
        source: CONTOUR_SOURCE_ID,
        'source-layer': CONTOUR_SOURCE_LAYER,
        minzoom: 12.8,
        filter: ['>', ['get', 'level'], 0],
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 480,
          'text-field': ['concat', ['number-format', ['get', 'ele'], {}], ' m'],
          'text-font': ['Noto Sans Italic'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 12.8, 8.8, 13, 9, 16, 10.8],
          visibility: 'visible',
        },
        paint: {
          'text-color': 'rgba(99, 73, 36, 0.76)',
          'text-halo-color': 'rgba(248, 244, 235, 0.86)',
          'text-halo-width': 1,
          'text-opacity': ['interpolate', ['linear'], ['zoom'], 12.8, 0.38, 13, 0.48, 15, 0.78],
        },
      },
      labelBeforeLayerId,
    )
  }
}

type SettleProbeMap = Pick<maplibregl.Map, 'getStyle' | 'getSource' | 'isSourceLoaded'>

/**
 * True once every style source except the gated contour source has finished
 * loading its tiles — the signal that the basemap has caught up after a camera
 * move and deferred contour tiles may load without competing for resources.
 */
export function areRoutecampBasemapSourcesLoaded(map: SettleProbeMap): boolean {
  const sources = map.getStyle()?.sources ?? {}
  return Object.keys(sources).every((sourceId) => {
    if (sourceId === CONTOUR_SOURCE_ID) {
      return true
    }
    // Guard: isSourceLoaded fires a map error event for unknown ids.
    if (!map.getSource(sourceId)) {
      return true
    }
    return map.isSourceLoaded(sourceId)
  })
}

export function applyRoutecampContours(map: RuntimeMap): boolean {
  if (!supportsRoutecampContours(map)) {
    if (import.meta.env.DEV && typeof logService.debug === 'function') {
      logService.debug(LOG_CATEGORIES.MAP, 'Routecamp contour source not available for style', {
        sourceIds: Object.keys(map.getStyle().sources ?? {}),
      })
    }
    return false
  }

  if (contourLayerIds.every((layerId) => Boolean(map.getLayer(layerId)))) {
    return false
  }

  addContourSource(map)
  addContourLayers(map)
  return true
}
